import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { PageHeader } from "../../components/PageHeader";
import { foyerApi } from "../../http/foyer";
import { tokens } from "../../theme";
import {
  ChangeBadge,
  KpiTile,
  TrendLegend,
  TrendLine,
} from "../../components/playbackAnalytics/charts";
import { usePlaybackFormat } from "../../components/playbackAnalytics/format";
import type {
  MoneyMinor,
  PortalRentalsReport,
} from "@shared/types/portalRentals";
import { useFormat, type Formatters } from "../../i18n/format";

/**
 * Rentals on YOUR purchase portal: everything sold there, whoever's title it
 * was - a portal that carries another studio's catalogue sold those rentals
 * too. (The Revenue page is the other question: what YOUR titles earned on
 * any portal.) See `@shared/types/portalRentals`.
 */

const RANGES = ["7", "30", "90", "365", "custom"] as const;

type Metric = "rentals" | "sold" | "plays";

/** Today's date in Stockholm as `YYYY-MM-DD`, the API's day key. */
function today(): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtMoney(f: Formatters, minor: number, currency: string): string {
  return f.currency(minor / 100, currency, {
    maximumFractionDigits: minor % 100 === 0 ? 0 : 2,
  });
}

function fmtMoneyList(f: Formatters, list: MoneyMinor[]): string {
  return list.length
    ? list.map((m) => fmtMoney(f, m.amountMinor, m.currency)).join(" + ")
    : "–";
}

function amountIn(list: MoneyMinor[], currency: string): number {
  return list.find((m) => m.currency === currency)?.amountMinor ?? 0;
}

export default function SupplierRentalsPage() {
  const [params, setParams] = useSearchParams();
  const { t: tr } = useTranslation(["supplier", "common"]);
  const range = params.get("range") ?? "30";
  const t = today();
  const to = range === "custom" ? params.get("to") || t : t;
  const from =
    range === "custom"
      ? params.get("from") || shiftDay(to, -29)
      : shiftDay(to, -((Number(range) || 30) - 1));

  const [data, setData] = useState<PortalRentalsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>("rentals");

  useEffect(() => {
    let cancelled = false;
    setError(null);
    foyerApi
      .ownerRentals({ from, to })
      .then((r) => !cancelled && setData(r))
      .catch(
        (err) =>
          !cancelled &&
          setError(
            err instanceof Error
              ? err.message
              : tr("supplierRentalsPage.loadFailed")
          )
      );
    return () => {
      cancelled = true;
    };
  }, [from, to, tr]);

  const setParam = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };

  const comparable = useMemo(() => {
    if (!data) return false;
    // Plays are only kept 90 days; rentals forever. Compare only when the
    // previous period still has its plays.
    return data.previousRange.from >= shiftDay(t, -data.playsRetentionDays);
  }, [data, t]);

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={tr("supplierRentalsPage.eyebrow")}
        title={tr("supplierRentalsPage.title")}
        subtitle={tr("supplierRentalsPage.subtitle")}
        actions={
          <Button
            component="a"
            href={foyerApi.ownerRentalsCsvUrl({ from, to })}
            startIcon={<DownloadOutlinedIcon />}
            variant="outlined"
            disabled={!data}
            data-testid="rentals-export"
          >
            {tr("supplierRentalsPage.exportCsv")}
          </Button>
        }
      />

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={tr("supplierRentalsPage.period")}
          value={range}
          onChange={(e) =>
            setParam(
              e.target.value === "custom"
                ? { range: "custom", from, to }
                : { range: e.target.value, from: null, to: null }
            )
          }
          sx={{ minWidth: 170 }}
          data-testid="rentals-range"
        >
          {RANGES.map((r) => (
            <MenuItem key={r} value={r}>
              {tr(`supplierRentalsPage.ranges.${r}`)}
            </MenuItem>
          ))}
        </TextField>
        {range === "custom" && (
          <>
            <TextField
              size="small"
              type="date"
              label={tr("common:labels.from")}
              value={from}
              onChange={(e) => setParam({ from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label={tr("common:labels.to")}
              value={to}
              onChange={(e) => setParam({ to: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" data-testid="rentals-error">
          {error}
        </Alert>
      )}
      {!data && !error && <CircularProgress size={24} />}

      {data && !data.hasPortal && (
        <Alert severity="info" data-testid="rentals-no-portal">
          {tr("supplierRentalsPage.noPortal")}
        </Alert>
      )}

      {data && (
        <RentalsView
          data={data}
          metric={metric}
          setMetric={setMetric}
          comparable={comparable}
        />
      )}
    </Stack>
  );
}

function RentalsView({
  data,
  metric,
  setMetric,
  comparable,
}: {
  data: PortalRentalsReport;
  metric: Metric;
  setMetric: (m: Metric) => void;
  comparable: boolean;
}) {
  const { t: tr } = useTranslation(["supplier", "common"]);
  const f = useFormat();
  const pf = usePlaybackFormat();
  const t = data.totals;
  const p = data.previous;
  const before = <T,>(v: T) => (comparable ? v : undefined);
  const cur = data.currency;

  const pick: Record<
    Metric,
    (d: PortalRentalsReport["series"][number]) => number
  > = {
    rentals: (d) => d.rentals,
    sold: (d) => d.soldMinor,
    plays: (d) => d.plays,
  };
  const fmt: Record<Metric, (v: number) => string> = {
    rentals: pf.count,
    sold: (v) => fmtMoney(f, v, cur),
    plays: pf.count,
  };
  const label = (m: Metric) => tr(`supplierRentalsPage.metrics.${m}`);

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
        }}
      >
        <KpiTile
          label={label("rentals")}
          value={pf.count(t.rentals)}
          now={t.rentals}
          before={before(p.rentals)}
          better="up"
          hint={tr("supplierRentalsPage.titlesCount", {
            count: pf.count(t.titlesRented),
          })}
          selected={metric === "rentals"}
          onClick={() => setMetric("rentals")}
          testId="rentals-kpi-rentals"
        />
        <KpiTile
          label={label("sold")}
          value={fmtMoneyList(f, t.sold)}
          now={amountIn(t.sold, cur)}
          before={before(amountIn(p.sold, cur))}
          better="up"
          hint={tr("supplierRentalsPage.grossInclVat")}
          selected={metric === "sold"}
          onClick={() => setMetric("sold")}
          testId="rentals-kpi-sold"
        />
        <KpiTile
          label={tr("supplierRentalsPage.customers")}
          value={pf.count(t.customers)}
          now={t.customers}
          before={before(p.customers)}
          better="up"
          testId="rentals-kpi-customers"
        />
        <KpiTile
          label={label("plays")}
          value={pf.count(t.plays)}
          now={t.plays}
          before={before(p.plays)}
          better="up"
          hint={tr("supplierRentalsPage.customersWatched", {
            count: pf.count(t.viewers),
          })}
          selected={metric === "plays"}
          onClick={() => setMetric("plays")}
          testId="rentals-kpi-plays"
        />
        <KpiTile
          label={tr("supplierRentalsPage.refunds")}
          value={pf.count(t.refunds)}
          now={t.refunds}
          before={before(p.refunds)}
          better="down"
          hint={t.refunds ? fmtMoneyList(f, t.refunded) : undefined}
          testId="rentals-kpi-refunds"
        />
      </Box>

      <Box
        sx={{
          border: `1px solid ${tokens.line}`,
          borderRadius: 1,
          p: 2,
          bgcolor: tokens.surface,
        }}
        data-testid="rentals-trend"
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle1">
            {tr("supplierRentalsPage.perDay", { metric: label(metric) })}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, v: Metric | null) => v && setMetric(v)}
          >
            <ToggleButton value="rentals">{label("rentals")}</ToggleButton>
            <ToggleButton value="sold">{label("sold")}</ToggleButton>
            <ToggleButton value="plays">{label("plays")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <TrendLine
          points={data.series.map((d) => ({
            label: pf.bucket(d.day, "day"),
            value: pick[metric](d),
          }))}
          format={fmt[metric]}
          showPrevious={false}
          ariaLabel={tr("supplierRentalsPage.perDay", {
            metric: label(metric),
          })}
        />
        <Box sx={{ mt: 1 }}>
          <TrendLegend current={`${data.range.from} – ${data.range.to}`} />
        </Box>
      </Box>

      <Box
        sx={{
          border: `1px solid ${tokens.line}`,
          borderRadius: 1,
          p: 2,
          bgcolor: tokens.surface,
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {tr("supplierRentalsPage.perTitle")}
        </Typography>
        {data.titles.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="rentals-empty"
          >
            {tr("supplierRentalsPage.empty")}
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="rentals-titles">
              <TableHead>
                <TableRow>
                  <TableCell>{tr("common:labels.title")}</TableCell>
                  <TableCell align="right">{label("rentals")}</TableCell>
                  {comparable && (
                    <TableCell align="right">
                      {tr("supplierRentalsPage.columns.vsBefore")}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {tr("supplierRentalsPage.columns.sold", { currency: cur })}
                  </TableCell>
                  <TableCell align="right">
                    {tr("supplierRentalsPage.refunds")}
                  </TableCell>
                  <TableCell align="right">{label("plays")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.titles.map((r) => (
                  <TableRow
                    key={r.assetId}
                    data-testid={`rentals-title-${r.assetId}`}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 28,
                            height: 40,
                            flexShrink: 0,
                            borderRadius: 0.5,
                            bgcolor: tokens.rowAlt,
                            backgroundImage: r.imageUrl
                              ? `url("${r.imageUrl}")`
                              : "none",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="bodySm"
                            sx={{ fontWeight: "fontWeightMedium" }}
                          >
                            {r.title}
                          </Typography>
                          {!r.own && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={tr("supplierRentalsPage.carried", {
                                owner: r.ownerName,
                              })}
                              sx={{ mt: 0.25 }}
                            />
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{pf.count(r.rentals)}</TableCell>
                    {comparable && (
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          <ChangeBadge
                            now={r.rentals}
                            before={r.previousRentals}
                            better="up"
                          />
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      {r.soldMinor
                        ? fmtMoney(f, r.soldMinor, cur)
                        : tr("common:labels.noValue")}
                    </TableCell>
                    <TableCell align="right">
                      {r.refunds
                        ? pf.count(r.refunds)
                        : tr("common:labels.noValue")}
                    </TableCell>
                    <TableCell align="right">{pf.count(r.plays)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" component="p">
        {tr("supplierRentalsPage.footnote", {
          days: data.playsRetentionDays,
          comparison: comparable
            ? tr("supplierRentalsPage.comparedWith", {
                from: pf.day(data.previousRange.from),
                to: pf.day(data.previousRange.to),
              })
            : tr("supplierRentalsPage.noComparison"),
        })}
      </Typography>
    </Stack>
  );
}
